import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';

import type { GapReviewComment } from '@momentum/types';

import { clipQuote, findQuoteRange } from '../lib/quote-range';

/** A selection waiting on the reviewer to say whether it is worth a comment. */
export interface PendingQuote {
  quote: string;
  /** Viewport coordinates of the right-click that captured it. */
  x: number;
  y: number;
}

/** The comments on the passage under the pointer, and where the pointer was. */
export interface HoveredQuote {
  quote: string;
  comments: GapReviewComment[];
  /** Viewport coordinates, for the tooltip to sit beside. */
  x: number;
  y: number;
}

export interface QuoteComments {
  /** Goes on the element wrapping the reader's document. */
  docRef: RefObject<HTMLDivElement | null>;
  onContextMenu: (mouseEvent: ReactMouseEvent) => void;
  /** The quotes the document on screen actually contains right now. */
  matchedQuotes: ReadonlySet<string>;
  /** Scrolls a quoted passage into view and flashes it. */
  revealQuote: (quote: string) => void;
  /** The selection a right-click captured, until it is taken or dropped. */
  pending: PendingQuote | undefined;
  /** Hands the pending passage to the composer. */
  acceptPending: () => void;
  dismissPending: () => void;
  /** What to say about the passage under the pointer, if it carries comments. */
  hovered: HoveredQuote | undefined;
}

/**
 * The names the three highlights are registered under. Global to the document —
 * the API has one registry — which is why all of them carry the feature's prefix.
 */
const QUOTE_HIGHLIGHT = 'gr-quote';
const DRAFT_QUOTE_HIGHLIGHT = 'gr-quote-draft';
const ACTIVE_QUOTE_HIGHLIGHT = 'gr-quote-active';

/**
 * Which highlight wins where two of them cover the same characters, which is
 * the normal case: a draft quote is usually being written about a passage
 * somebody already commented on, and revealing one paints over both.
 *
 * Without this the winner would be whichever was registered last, which changes
 * as comments are posted.
 */
const DRAFT_PRIORITY = 1;
const ACTIVE_PRIORITY = 2;

/** How long a revealed passage stays lit. Long enough to find with the eye. */
const FLASH_MS = 1400;

/**
 * The two methods this feature uses on `CSS.highlights`.
 *
 * The registry is a maplike, but TypeScript 5.9's `lib.dom` generates only
 * `forEach` off it — `set` and `delete` are absent from `HighlightRegistry`.
 * Narrowed here once rather than cast at each of the call sites.
 */
interface HighlightRegistryApi {
  set(name: string, highlight: Highlight): void;
  delete(name: string): void;
}

/**
 * Highlights are drawn with the CSS Custom Highlight API, which is why nothing
 * here wraps anything in a `<mark>`.
 *
 * The reader's document is React-rendered prose. Injecting elements into it
 * would mean mutating a tree React owns and re-injecting after every render,
 * and a comment that spans the end of one sentence and the start of the next
 * would need splitting across whatever markup sits between them. Ranges do not
 * touch the DOM at all: they are handed to the browser, which paints them, and
 * a re-render simply drops them.
 *
 * The cost is that a highlight cannot be hovered or clicked — the painted range
 * is not a hit target — so both of those are done by hand below, against the
 * rectangles the ranges report.
 *
 * `undefined` where the API is missing (jsdom, older Safari). Everything else
 * still works there; the passages simply are not painted.
 */
function highlightRegistry(): HighlightRegistryApi | undefined {
  if (typeof CSS === 'undefined' || !('highlights' in CSS)) return undefined;
  return CSS.highlights as unknown as HighlightRegistryApi;
}

/** A resolved anchor: the stored quote and where it currently sits on screen. */
interface ResolvedQuote {
  quote: string;
  range: Range;
}

interface UseQuoteCommentsOptions {
  /** The current draft's comments — only the anchored ones are used. */
  comments: GapReviewComment[];
  /**
   * Changes whenever the document on screen is a different document: another
   * draft, the reader's other view, the edit form. Highlights are resolved
   * against what is rendered, so they are recomputed exactly when it changes.
   */
  documentKey: string;
  /**
   * The passage the unposted comment is anchored to, held by the surface. It is
   * painted too, and in its own colour: between right-clicking a sentence and
   * posting about it the reviewer is looking at a textarea, and the selection
   * they made goes grey the moment the caret lands there.
   */
  draftQuote: string | undefined;
  /**
   * Called with the captured passage when the reviewer takes the right-click
   * menu up on it. The hook does not hold it past that point: the passage is one
   * half of a comment the surface is already holding the other half of, and
   * splitting them across two owners is how they would come apart.
   */
  onQuoteCaptured: (quote: string) => void;
}

/**
 * Anchored comments over the reader: capturing a selection, painting the
 * passages that carry one, saying what they say on hover, and walking back from
 * a comment to its text.
 */
export function useQuoteComments({
  comments,
  documentKey,
  draftQuote,
  onQuoteCaptured,
}: UseQuoteCommentsOptions): QuoteComments {
  const docRef = useRef<HTMLDivElement | null>(null);
  const [matchedQuotes, setMatchedQuotes] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [pending, setPending] = useState<PendingQuote | undefined>(undefined);
  const [hovered, setHovered] = useState<HoveredQuote | undefined>(undefined);

  // Through refs so the two document-level handlers stay stable: they are bound
  // to a reader that re-renders on every keystroke in the composer.
  const onQuoteCapturedRef = useRef(onQuoteCaptured);
  onQuoteCapturedRef.current = onQuoteCaptured;
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const hoveredQuoteRef = useRef<string | undefined>(undefined);
  hoveredQuoteRef.current = hovered?.quote;

  /** Where each anchored comment's passage currently sits in the document. */
  const quoteRangesRef = useRef<ResolvedQuote[]>([]);

  /**
   * The anchored quotes, deduplicated and serialised so the effect below re-runs
   * when a comment is posted but not on every render of the queue around it.
   * Serialised rather than joined: a quote is a whole passage, spaces and all.
   */
  const quotesKey = useMemo(() => {
    const uniqueQuotes = new Set<string>();
    for (const comment of comments) {
      if (comment.quote !== undefined) uniqueQuotes.add(comment.quote);
    }
    return JSON.stringify([...uniqueQuotes]);
  }, [comments]);

  useEffect(() => {
    const registry = highlightRegistry();
    const doc = docRef.current;
    quoteRangesRef.current = [];
    setHovered(undefined);

    if (!doc) {
      registry?.delete(QUOTE_HIGHLIGHT);
      setMatchedQuotes(new Set());
      return;
    }

    const quotes = JSON.parse(quotesKey) as string[];
    const resolved: ResolvedQuote[] = [];
    for (const quote of quotes) {
      const range = findQuoteRange(doc, quote);
      if (!range) continue;
      resolved.push({ quote, range });
    }

    quoteRangesRef.current = resolved;
    setMatchedQuotes(new Set(resolved.map(({ quote }) => quote)));

    if (!registry) return;
    if (resolved.length === 0) registry.delete(QUOTE_HIGHLIGHT);
    else {
      registry.set(
        QUOTE_HIGHLIGHT,
        new Highlight(...resolved.map(({ range }) => range))
      );
    }

    return () => registry.delete(QUOTE_HIGHLIGHT);
  }, [documentKey, quotesKey]);

  // The passage the composer is currently about, kept marked in the reader for
  // as long as it is being written about. This is the answer to "which sentence
  // did I right-click" — the one question a reviewer typing into a panel on the
  // other side of the screen cannot answer from the panel alone.
  useEffect(() => {
    const registry = highlightRegistry();
    if (!registry) return;

    const doc = docRef.current;
    const range =
      doc && draftQuote !== undefined ? findQuoteRange(doc, draftQuote) : null;
    if (!range) {
      registry.delete(DRAFT_QUOTE_HIGHLIGHT);
      return;
    }

    const highlight = new Highlight(range);
    highlight.priority = DRAFT_PRIORITY;
    registry.set(DRAFT_QUOTE_HIGHLIGHT, highlight);

    return () => registry.delete(DRAFT_QUOTE_HIGHLIGHT);
  }, [documentKey, draftQuote]);

  /**
   * Hovering a commented passage. Bound to the window rather than to the reader
   * because leaving the document has to clear the tooltip as reliably as
   * entering a passage raises it, and the reader's wrapper is `display:
   * contents` — it has no box of its own to leave.
   */
  useEffect(() => {
    let queuedFrame = 0;
    let latestPoint: {
      x: number;
      y: number;
      target: EventTarget | null;
    } | null = null;

    const hitTest = () => {
      queuedFrame = 0;
      const point = latestPoint;
      const doc = docRef.current;
      const resolvedQuotes = quoteRangesRef.current;

      // The right-click menu is a decision in progress; a tooltip about some
      // other comment appearing under it is noise on top of it.
      const isOverDocument =
        point !== null &&
        doc !== null &&
        resolvedQuotes.length > 0 &&
        pendingRef.current === undefined &&
        point.target instanceof Node &&
        doc.contains(point.target);

      if (!isOverDocument) {
        if (hoveredQuoteRef.current !== undefined) setHovered(undefined);
        return;
      }

      // Measured fresh rather than cached. `getClientRects()` costs a layout
      // flush, which is why this runs once a frame instead of once a move — but
      // a cache would have to be invalidated by everything that shifts the
      // reader, and dragging a pane divider fires no event that says so.
      const hit = resolvedQuotes.find(({ range }) =>
        // `Array.from` rather than a spread: `DOMRectList` is an array-like
        // that this TypeScript lib target does not declare as iterable.
        Array.from(range.getClientRects()).some(
          (rect) =>
            point.x >= rect.left &&
            point.x <= rect.right &&
            point.y >= rect.top &&
            point.y <= rect.bottom
        )
      );

      if (hit === undefined) {
        if (hoveredQuoteRef.current !== undefined) setHovered(undefined);
        return;
      }
      // Already showing this passage: the tooltip stays where it came up rather
      // than chasing the pointer across the sentence it is explaining.
      if (hit.quote === hoveredQuoteRef.current) return;

      setHovered({
        quote: hit.quote,
        comments: commentsRef.current.filter(
          (comment) => comment.quote === hit.quote
        ),
        x: point.x,
        y: point.y,
      });
    };

    const onPointerMove = (pointerEvent: MouseEvent) => {
      latestPoint = {
        x: pointerEvent.clientX,
        y: pointerEvent.clientY,
        target: pointerEvent.target,
      };
      if (queuedFrame === 0) queuedFrame = requestAnimationFrame(hitTest);
    };

    window.addEventListener('mousemove', onPointerMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onPointerMove);
      if (queuedFrame !== 0) cancelAnimationFrame(queuedFrame);
    };
  }, []);

  const onContextMenu = useCallback((mouseEvent: ReactMouseEvent) => {
    const selection = window.getSelection();
    const doc = docRef.current;
    // No selection means the reviewer wants their browser's menu — Inspect,
    // copy link, translate. Only a selection inside the gap is claimed.
    if (!selection || selection.isCollapsed || !doc) return;
    if (
      !selection.anchorNode ||
      !selection.focusNode ||
      !doc.contains(selection.anchorNode) ||
      !doc.contains(selection.focusNode)
    ) {
      return;
    }

    const quote = clipQuote(selection.toString());
    if (quote.length === 0) return;

    mouseEvent.preventDefault();
    setHovered(undefined);
    setPending({
      quote,
      x: mouseEvent.clientX,
      y: mouseEvent.clientY,
    });
  }, []);

  const acceptPending = useCallback(() => {
    const captured = pendingRef.current;
    if (captured === undefined) return;
    setPending(undefined);
    // The selection has done its job and the draft highlight takes over from
    // here — it survives the caret moving to the composer, which is where the
    // reviewer is about to be, and a browser selection does not.
    window.getSelection()?.removeAllRanges();
    onQuoteCapturedRef.current(captured.quote);
  }, []);

  const dismissPending = useCallback(() => setPending(undefined), []);

  // Walking the queue, or switching what the reader is showing, drops a menu
  // that is about a passage no longer on screen.
  useEffect(() => setPending(undefined), [documentKey]);

  const revealQuote = useCallback((quote: string) => {
    const doc = docRef.current;
    if (!doc) return;
    const range = findQuoteRange(doc, quote);
    if (!range) return;

    // The range itself has no `scrollIntoView`; the element holding its first
    // character does, and it is by construction the passage's own line.
    //
    // Instant, not smooth. The reviewer clicked to look at something, and the
    // flash below is what draws the eye to it once it is there — an animation
    // in between is a second of waiting to be told what was already asked for.
    const anchorElement =
      range.startContainer.parentElement ?? doc.firstElementChild;
    anchorElement?.scrollIntoView({ block: 'center' });

    const registry = highlightRegistry();
    if (!registry) return;
    const flash = new Highlight(range);
    flash.priority = ACTIVE_PRIORITY;
    registry.set(ACTIVE_QUOTE_HIGHLIGHT, flash);
    window.setTimeout(() => registry.delete(ACTIVE_QUOTE_HIGHLIGHT), FLASH_MS);
  }, []);

  useEffect(() => {
    const registry = highlightRegistry();
    return () => registry?.delete(ACTIVE_QUOTE_HIGHLIGHT);
  }, []);

  return {
    docRef,
    onContextMenu,
    matchedQuotes,
    revealQuote,
    pending,
    acceptPending,
    dismissPending,
    hovered,
  };
}
